// Keep editing state separate from server loads, navigation and successful saves.
const metadata = new Set(['_compact', 'createdAt', 'updatedAt']);
const plain = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
export function sameRecordValue(a, b) {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) || Array.isArray(b)) return Array.isArray(a) && Array.isArray(b)
    && a.length === b.length && a.every((value, index) => sameRecordValue(value, b[index]));
  if (!plain(a) || !plain(b)) return false;
  const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])];
  return keys.every((key) => sameRecordValue(a[key], b[key]));
}
export function hasRecordChanges(state) {
  return [...new Set([...Object.keys(state.baseline), ...Object.keys(state.value)])]
    .some((key) => !metadata.has(key) && !sameRecordValue(state.baseline[key], state.value[key]));
}
// Arrays are atomic to preserve additions/removals. Objects are rebased by field.
export function rebaseRecordDraft(before, draft, incoming) {
  if (sameRecordValue(before, draft)) return incoming;
  if (!plain(before) || !plain(draft) || !plain(incoming)) return draft;
  const next = { ...incoming };
  for (const key of new Set([...Object.keys(before), ...Object.keys(draft)])) {
    if (sameRecordValue(before[key], draft[key])) continue;
    if (!Object.prototype.hasOwnProperty.call(draft, key)) delete next[key];
    else next[key] = rebaseRecordDraft(before[key], draft[key], incoming[key]);
  }
  return next;
}
export const createRecordDraft = (value) => ({ value, baseline: value, generation: 0 });
const identity = (value) => String(value?.id ?? '');
export function recordDraftReducer(state, action) {
  if (action.type === 'edit') {
    const value = typeof action.value === 'function' ? action.value(state.value) : action.value;
    return { ...state, value };
  }
  if (action.type === 'reset' || action.type === 'receive') {
    const value = typeof action.value === 'function' ? action.value(action.type === 'receive' ? state.baseline : state.value) : action.value;
    // A list refresh cannot erase locally edited fields in the open record.
    if (action.type === 'receive' && identity(value) === identity(state.value) && hasRecordChanges(state)) {
      return { ...state, baseline: value, value: rebaseRecordDraft(state.baseline, state.value, value) };
    }
    return { value, baseline: value, generation: state.generation + (action.type === 'reset' || identity(value) !== identity(state.value) ? 1 : 0) };
  }
  if (action.type === 'saved') {
    if (action.generation !== state.generation || identity(action.sent) !== identity(state.value)) return state;
    // Do not mark edits made while the request was pending as already saved.
    const baseline = { ...action.sent, ...action.saved };
    return { ...state, baseline, value: rebaseRecordDraft(action.sent, state.value, baseline) };
  }
  return state;
}
